import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAppPreferences } from "../lib/preferences";

export interface ConfirmRequest {
  cancelButtonLabel?: string;
  confirmButtonLabel?: string;
  description: string;
  title: string;
}

type PendingRequest = ConfirmRequest & {
  resolve: (value: boolean) => void;
};

export function useConfirm() {
  const { t } = useAppPreferences();
  const [pendingRequest, setPendingRequest] = useState<PendingRequest | null>(null);
  const pendingRequestRef = useRef<PendingRequest | null>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const inertSiblingsRef = useRef<Array<{ element: HTMLElement; inert: boolean; ariaHidden: string | null }>>([]);
  const titleId = useId();
  const descriptionId = useId();

  const resetDialog = () => {
    pendingRequestRef.current = null;
    setPendingRequest(null);
  };

  const closeDialog = (result: boolean) => {
    const activeRequest = pendingRequestRef.current;
    if (!activeRequest) {
      return;
    }

    activeRequest.resolve(result);
    resetDialog();
  };

  useEffect(() => {
    if (!pendingRequest) {
      const restoreTarget = restoreFocusRef.current;
      restoreFocusRef.current = null;
      if (restoreTarget?.isConnected) restoreTarget.focus();
      return;
    }

    cancelButtonRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const siblings = Array.from(document.body.children)
      .filter((element): element is HTMLElement => element instanceof HTMLElement)
      .filter((element) => !dialogRef.current || !element.contains(dialogRef.current));
    inertSiblingsRef.current = siblings.map((element) => ({
      ariaHidden: element.getAttribute("aria-hidden"),
      element,
      inert: Boolean(element.inert),
    }));
    siblings.forEach((element) => {
      element.inert = true;
      element.setAttribute("aria-hidden", "true");
    });

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeDialog(false);
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
        "button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex=\"-1\"])",
      );
      if (!focusable || focusable.length === 0) {
        event.preventDefault();
        cancelButtonRef.current?.focus();
        return;
      }

      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
      inertSiblingsRef.current.forEach(({ ariaHidden, element, inert }) => {
        element.inert = inert;
        if (ariaHidden === null) element.removeAttribute("aria-hidden");
        else element.setAttribute("aria-hidden", ariaHidden);
      });
      inertSiblingsRef.current = [];
    };
  }, [pendingRequest]);

  useEffect(() => {
    pendingRequestRef.current = pendingRequest;
  }, [pendingRequest]);

  useEffect(() => {
    return () => {
      pendingRequestRef.current?.resolve(false);
    };
  }, []);

  const confirm = (request: ConfirmRequest) => {
    if (pendingRequestRef.current) {
      pendingRequestRef.current.resolve(false);
    }

    restoreFocusRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;

    return new Promise<boolean>((resolve) => {
      const nextRequest = {
        ...request,
        resolve,
      };
      pendingRequestRef.current = nextRequest;
      setPendingRequest(nextRequest);
    });
  };

  const dialogContent = pendingRequest ? (
    <div
      aria-hidden={false}
      className="ops-dialog-backdrop"
      onClick={() => closeDialog(false)}
      role="presentation"
    >
      <div
        aria-describedby={descriptionId}
        aria-labelledby={titleId}
        aria-modal="true"
        className="ops-dialog"
        onClick={(event) => event.stopPropagation()}
        ref={dialogRef}
        role="dialog"
      >
        <div className="ops-dialog-header">
          <p className="ops-kicker">{t("Confirmation")}</p>
          <h2 className="ops-dialog-title" id={titleId}>{pendingRequest.title}</h2>
          <p className="ops-copy !text-[var(--color-text-soft)]" id={descriptionId}>{pendingRequest.description}</p>
        </div>

        <div className="ops-dialog-actions">
          <button
            className="ops-button"
            data-variant="secondary"
            onClick={() => closeDialog(false)}
            ref={cancelButtonRef}
            type="button"
          >
            {pendingRequest.cancelButtonLabel ?? t("Cancel")}
          </button>
          <button
            className="ops-button"
            data-variant="danger"
            onClick={() => closeDialog(true)}
            type="button"
          >
            {pendingRequest.confirmButtonLabel ?? t("Discard changes")}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  const dialog = dialogContent && typeof document !== "undefined"
    ? createPortal(dialogContent, document.body)
    : dialogContent;

  return {
    confirm,
    dialog,
  };
}
